// Synthesized combat SFX via Web Audio — no audio assets, everything is
// oscillators and filtered noise (approach borrowed from the spy project).
// The AudioContext can only start after a user gesture; call unlock() on the
// first click.

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  unlock(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private tone(
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    gain: number,
    delay = 0
  ): void {
    if (!this.ctx || !this.master || gain <= 0.001) return;
    const t = this.ctx.currentTime + delay;
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(Math.max(1, f0), t);
    o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private noise(dur: number, freq: number, q: number, gain: number, delay = 0): void {
    if (!this.ctx || !this.master || gain <= 0.001) return;
    const t = this.ctx.currentTime + delay;
    const len = Math.max(1, Math.ceil(this.ctx.sampleRate * dur));
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.frequency.value = freq;
    f.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(f).connect(g).connect(this.master);
    src.start(t);
  }

  swing(heavy: boolean, vol: number): void {
    this.noise(heavy ? 0.26 : 0.15, heavy ? 500 : 900, 1.1, 0.5 * vol);
  }

  hit(vol: number): void {
    this.tone('sine', 150, 55, 0.18, 0.7 * vol);
    this.noise(0.09, 2200, 0.8, 0.3 * vol);
  }

  block(vol: number): void {
    this.tone('triangle', 950, 480, 0.09, 0.45 * vol);
    this.noise(0.06, 3200, 1.2, 0.35 * vol);
  }

  parry(vol: number): void {
    this.tone('sine', 1300, 2100, 0.28, 0.5 * vol);
    this.tone('sine', 2000, 2900, 0.22, 0.25 * vol, 0.03);
  }

  guardBreak(vol: number): void {
    this.noise(0.24, 650, 0.7, 0.7 * vol);
    this.tone('sine', 120, 45, 0.32, 0.7 * vol);
  }

  kick(vol: number): void {
    this.tone('sine', 95, 50, 0.14, 0.7 * vol);
    this.noise(0.07, 400, 0.9, 0.3 * vol);
  }

  dodge(vol: number): void {
    this.noise(0.16, 480, 0.7, 0.25 * vol);
  }

  death(vol: number): void {
    this.tone('sawtooth', 200, 38, 0.55, 0.4 * vol);
    this.noise(0.3, 300, 0.8, 0.4 * vol, 0.04);
  }

  respawn(vol: number): void {
    this.tone('sine', 500, 900, 0.2, 0.25 * vol);
  }

  arrow(vol: number): void {
    this.tone('triangle', 750, 320, 0.09, 0.25 * vol);
    this.noise(0.08, 2600, 1.5, 0.2 * vol);
  }

  alarm(): void {
    // urgent two-tone, always full volume
    this.tone('square', 620, 620, 0.16, 0.25);
    this.tone('square', 480, 480, 0.16, 0.25, 0.18);
    this.tone('square', 620, 620, 0.16, 0.25, 0.36);
  }

  horn(vol: number): void {
    // war horn: a low swelling fifth for new orders on the field
    this.tone('sawtooth', 146, 152, 0.5, 0.16 * vol);
    this.tone('sawtooth', 220, 226, 0.5, 0.1 * vol, 0.06);
  }

  bell(vol: number): void {
    // castle alarm bell: two slow, decaying strikes
    this.tone('triangle', 340, 310, 0.55, 0.35 * vol);
    this.tone('triangle', 452, 420, 0.4, 0.18 * vol, 0.02);
    this.tone('triangle', 340, 310, 0.55, 0.3 * vol, 0.5);
  }

  research(): void {
    this.tone('sine', 520, 780, 0.18, 0.3);
    this.tone('sine', 780, 1040, 0.22, 0.25, 0.12);
  }

  build(vol: number): void {
    this.tone('square', 220, 180, 0.07, 0.3 * vol);
    this.tone('square', 260, 200, 0.07, 0.3 * vol, 0.12);
    this.noise(0.06, 1200, 1, 0.25 * vol, 0.02);
  }
}
